import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Trans, useLingui } from '@lingui/react';
import { Button } from '@startuptools/ui/Button';
import { NavStepper } from '../../components/steppers/nav-stepper/NavStepper';
import { Divider } from '@startuptools/ui/Divider';
import { NavStepperStep } from '../../components/steppers/nav-stepper/NavStepperStep';
import { MeetingStepDetails } from '../meetings/components/edit-steps/Details';
import { MeetingKindHeader } from '../meetings/ShowInfo';
import { useShareholdersMeetingSuspenseQuery } from './ShareholdersMeeting.graphql';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { DateTime } from 'luxon';
import { MeetingStepLanguages } from '../meetings/components/edit-steps/Languages';
import { MeetingStepAgenda } from '../meetings/components/edit-steps/Agenda';
import { MeetingStepParticipants } from '../meetings/components/edit-steps/Participants';
import { MeetingStepEmailSummons } from '../meetings/components/edit-steps/EmailSummons';
import { meetingLinkShow } from '../../components/Links';
import { GMStepContactEmail } from './components/edit-steps/ContactEmail';
import { GMStepPostalVoting } from './components/edit-steps/PostalVoting';
import { GMStepVotingList } from './components/edit-steps/VotingList';
import { GMStepPowerOfAttorneys } from './components/edit-steps/PowerOfAttorneys';
import { GqlMeetingValidatorErrors, GqlSeverity } from '../../graphql/base-types.graphql';
import React from 'react';
import { useNavigate } from '../../components/Navigate';
import { Link } from 'react-router-dom';

const meetingIsDone = (meetingErrors: GqlMeetingValidatorErrors) => {
  return (
    meetingErrors.details.completed &&
    !meetingErrors.details.hasError &&
    meetingErrors.language.completed &&
    !meetingErrors.language.hasError &&
    meetingErrors.registerAttendance.completed &&
    !meetingErrors.registerAttendance.hasError &&
    meetingErrors.postalVote.completed &&
    !meetingErrors.postalVote.hasError &&
    meetingErrors.agenda.completed &&
    !meetingErrors.agenda.hasError &&
    meetingErrors.summons.completed &&
    !meetingErrors.summons.hasError &&
    meetingErrors.votingList.completed &&
    !meetingErrors.votingList.hasError &&
    meetingErrors.participants.completed &&
    !meetingErrors.participants.hasError &&
    meetingErrors.emailSummons.completed &&
    !meetingErrors.emailSummons.hasError
  );
};

export const ShareholdersMeetingEdit = ({ meetingId }: { meetingId: string }) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const { link } = useNavigate();
  const [_pending, startTransition] = React.useTransition();

  const {
    data: { meeting, meetingConcerns, agendaItems, meetingErrors },
    refetch: re,
  } = useShareholdersMeetingSuspenseQuery({ variables: { companyId, meetingId }, fetchPolicy: 'cache-and-network' });

  const refetch = React.useCallback(() => {
    startTransition(() => {
      void re();
    });
  }, [re, startTransition]);

  return (
    <Stack gap={3}>
      <Stack direction="row" gap={3} alignItems="center">
        <Typography level="h3">
          <MeetingKindHeader meeting={meeting} />
          {' - '}
          <DateFormat locale={i18n.locale} format={DateTime.DATE_SHORT}>
            {meeting.startTime}
          </DateFormat>
        </Typography>
        {meetingIsDone(meetingErrors) && (
          <>
            <Divider orientation="vertical" />
            <Button variant="outlined" size="sm" component={Link} to={link(meetingLinkShow(meeting))}>
              <Trans id="Go to Overview" />
            </Button>
          </>
        )}
      </Stack>

      <NavStepper path="*" onStepSaved={refetch}>
        <NavStepperStep path="details" label={<Trans id="Details" />} index validatorStatus={meetingErrors.details}>
          <MeetingStepDetails meeting={meeting} />
        </NavStepperStep>

        <NavStepperStep path="language" label={<Trans id="Language" />} validatorStatus={meetingErrors.language}>
          <MeetingStepLanguages meeting={meeting} />
        </NavStepperStep>

        <NavStepperStep
          path="registration"
          label={<Trans id="Registration" />}
          validatorStatus={meetingErrors.registerAttendance}
        >
          <GMStepContactEmail meeting={meeting} />
        </NavStepperStep>

        <NavStepperStep
          path="postal-vote"
          label={<Trans id="Postal Voting" />}
          validatorStatus={meetingErrors.postalVote}
        >
          <GMStepPostalVoting meeting={meeting} />
        </NavStepperStep>

        <NavStepperStep path="agenda" label={<Trans id="Agenda" />} validatorStatus={meetingErrors.agenda}>
          <MeetingStepAgenda meeting={meeting} meetingConcerns={meetingConcerns} agendaItems={agendaItems} />
        </NavStepperStep>

        <NavStepperStep
          path="voting-list"
          label={<Trans id="Voting List" />}
          validatorStatus={meetingErrors.votingList}
        >
          <GMStepVotingList meeting={meeting} />
        </NavStepperStep>

        <NavStepperStep
          path="participants"
          label={<Trans id="Other Participants" />}
          validatorStatus={meetingErrors.participants}
        >
          <MeetingStepParticipants meeting={meeting} />
        </NavStepperStep>

        {meeting.meetingParticipants.some(mp => mp.proxyId) && (
          <NavStepperStep
            path="power-of-attorneys"
            label={<Trans id="Power of Attorneys" />}
            validatorStatus={{ completed: true, hasError: false, severity: GqlSeverity.Warning }}
          >
            <GMStepPowerOfAttorneys meeting={meeting} />
          </NavStepperStep>
        )}

        <NavStepperStep path="summons" label={<Trans id="Summons" />} validatorStatus={meetingErrors.summons}>
          <MeetingStepEmailSummons meeting={meeting} refetch={refetch} />
        </NavStepperStep>
      </NavStepper>
    </Stack>
  );
};
